import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadSale} from '../../../models/zad'
import {ZadApiError} from '../zad/errors'
import {
  cancelZadSale,
  createZadSale,
  fetchZadSaleById,
  fetchZadSalePickerCustomers,
  fetchZadSalePickerProducts,
  fetchZadSalePickerStock,
  fetchZadSales,
  updateZadSale,
  validateAndCollectZadSale,
  validateZadSale,
} from './service'
import {FetchZadSalesResponse, ZadSaleState} from './types'

const initialState: ZadSaleState = {
  sales: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  selectedSale: null,
  pickerCustomers: [],
  pickerProducts: [],
  pickerStock: [],
  pickerLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
  errorDetails: null,
}

function applyRejected(state: ZadSaleState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
  state.errorDetails = payload.details || null
}

function clearError(state: ZadSaleState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
  state.errorDetails = null
}

function upsertSale(state: ZadSaleState, sale?: ZadSale | null) {
  if (!sale?._id) return
  state.selectedSale = sale
  const index = state.sales.findIndex((item) => item._id === sale._id)
  if (index >= 0) {
    state.sales[index] = sale
  }
}

const zadSaleSlice = createSlice({
  name: 'zadSales',
  initialState,
  reducers: {
    clearZadSaleError: (state: ZadSaleState) => {
      clearError(state)
    },
    clearSelectedZadSale: (state: ZadSaleState) => {
      state.selectedSale = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadSales.pending, (state: ZadSaleState) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadSales.rejected, (state: ZadSaleState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.listLoading = false
      state.listInitialized = true
    })
    builder.addCase(
      fetchZadSales.fulfilled,
      (state: ZadSaleState, action: PayloadAction<FetchZadSalesResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.sales = action.payload.sales
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadSaleById.pending, (state: ZadSaleState) => {
      state.actionLoading = true
    })
    builder.addCase(fetchZadSaleById.rejected, (state: ZadSaleState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(fetchZadSaleById.fulfilled, (state: ZadSaleState, action) => {
      state.actionLoading = false
      state.selectedSale = action.payload.sale
    })

    builder.addCase(createZadSale.pending, (state: ZadSaleState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(createZadSale.rejected, (state: ZadSaleState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(createZadSale.fulfilled, (state: ZadSaleState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertSale(state, action.payload.sale)
    })

    builder.addCase(updateZadSale.pending, (state: ZadSaleState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(updateZadSale.rejected, (state: ZadSaleState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(updateZadSale.fulfilled, (state: ZadSaleState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertSale(state, action.payload.sale)
    })

    builder.addCase(validateZadSale.pending, (state: ZadSaleState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(validateZadSale.rejected, (state: ZadSaleState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(validateZadSale.fulfilled, (state: ZadSaleState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertSale(state, action.payload.sale)
    })

    builder.addCase(validateAndCollectZadSale.pending, (state: ZadSaleState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(validateAndCollectZadSale.rejected, (state: ZadSaleState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(validateAndCollectZadSale.fulfilled, (state: ZadSaleState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertSale(state, action.payload.sale)
    })

    builder.addCase(cancelZadSale.pending, (state: ZadSaleState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(cancelZadSale.rejected, (state: ZadSaleState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(cancelZadSale.fulfilled, (state: ZadSaleState, action) => {
      state.actionLoading = false
      clearError(state)
      upsertSale(state, action.payload.sale)
    })

    builder.addCase(fetchZadSalePickerCustomers.pending, (state: ZadSaleState) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadSalePickerCustomers.rejected, (state: ZadSaleState) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadSalePickerCustomers.fulfilled, (state: ZadSaleState, action) => {
      state.pickerLoading = false
      state.pickerCustomers = action.payload.customers
    })

    builder.addCase(fetchZadSalePickerProducts.pending, (state: ZadSaleState) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadSalePickerProducts.rejected, (state: ZadSaleState) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadSalePickerProducts.fulfilled, (state: ZadSaleState, action) => {
      state.pickerLoading = false
      state.pickerProducts = action.payload.products
    })

    builder.addCase(fetchZadSalePickerStock.pending, (state: ZadSaleState) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadSalePickerStock.rejected, (state: ZadSaleState) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadSalePickerStock.fulfilled, (state: ZadSaleState, action) => {
      state.pickerLoading = false
      state.pickerStock = action.payload.items
    })
  },
})

export default zadSaleSlice.reducer
export const actions = zadSaleSlice.actions
export {initialState as zadSaleInitialState}
